/**
 * The monitoring screens' route registration: overview, agenda and failures,
 * discovered by `../routes.ts`'s glob. All three carry a campaign-nav entry,
 * nested under "Monitoramento" (frame 105:6) as its second level.
 */

import { createElement, type ReactNode } from 'react';
import { CalendarClock, CalendarDays, Gauge, TriangleAlert } from 'lucide-react';
import { useParams } from '@tanstack/react-router';

import type { RouteBundle } from '../../routes/contract';
import { useCampaignOrganisationId, useOrgContext } from '../../session/orgContext';
import { Agenda } from './Agenda';
import { Failures } from './Failures';
import { cm } from './messages';
import { MonitoringOverview } from './MonitoringOverview';
import { AGENDA_PATH, FAILURES_PATH, MONITORING_PATH } from './paths';

const CONTROL_PLANE_READ = 'control_plane.read';

function useParamCampaignId(): string {
  const { campaignId } = useParams({ strict: false }) as { campaignId?: string };
  return campaignId ?? '';
}

function MonitoringOverviewRoute(): ReactNode {
  const campaignId = useParamCampaignId();
  const context = useOrgContext();
  if (context.kind !== 'campaign') return null;
  return createElement(MonitoringOverview, {
    campaignId,
    tenantId: context.organisation.tenant_id,
    organisationName: context.organisation.tenant_name,
  });
}

function AgendaRoute(): ReactNode {
  const campaignId = useParamCampaignId();
  const tenantId = useCampaignOrganisationId();
  if (tenantId === null) return null;
  return createElement(Agenda, { campaignId, tenantId });
}

function FailuresRoute(): ReactNode {
  const campaignId = useParamCampaignId();
  return createElement(Failures, { campaignId });
}

export const bundle: RouteBundle = {
  routes: [
    {
      path: MONITORING_PATH,
      element: createElement(MonitoringOverviewRoute),
      requires: CONTROL_PLANE_READ,
      kind: 'data',
    },
    {
      path: AGENDA_PATH,
      element: createElement(AgendaRoute),
      requires: CONTROL_PLANE_READ,
      kind: 'data',
    },
    {
      path: FAILURES_PATH,
      element: createElement(FailuresRoute),
      requires: CONTROL_PLANE_READ,
      kind: 'data',
    },
  ],
  nav: [],
  campaignNav: [
    {
      path: MONITORING_PATH,
      label: () => cm('nav.label'),
      requires: CONTROL_PLANE_READ,
      icon: CalendarClock,
      order: 80,
      kind: 'data',
    },
    {
      path: MONITORING_PATH,
      label: () => cm('nav.overview'),
      requires: CONTROL_PLANE_READ,
      parent: MONITORING_PATH,
      icon: Gauge,
      order: 81,
      kind: 'data',
    },
    {
      path: AGENDA_PATH,
      label: () => cm('agenda.title'),
      requires: CONTROL_PLANE_READ,
      parent: MONITORING_PATH,
      icon: CalendarDays,
      order: 82,
      kind: 'data',
    },
    {
      path: FAILURES_PATH,
      label: () => cm('failures.page.title'),
      requires: CONTROL_PLANE_READ,
      parent: MONITORING_PATH,
      icon: TriangleAlert,
      order: 83,
      kind: 'data',
    },
  ],
};
